import { useCallback, useEffect, useRef, useState } from 'react'
import { supabase, callFn } from '../lib/supabase'

/** The reports login (see supabase/functions/_shared/reports-auth.ts). A session for this user is a Reports session. */
export const REPORTS_EMAIL = (import.meta.env.VITE_REPORTS_LOGIN_EMAIL as string | undefined) || 'reports@starchivedesk.store'
const UNTIL_KEY = 'starchive.reports.until'
const TAB_KEY = 'starchive.reports.tab'
export const KEEP_HOURS = 12

export type Row = Record<string, unknown>

function deviceId() {
  try { let d = localStorage.getItem('starchive.device') ?? ''; if (!d) { d = crypto.randomUUID(); localStorage.setItem('starchive.device', d) } return d } catch { return '' }
}

/** Sign in with an owner's employee ID and Reports PIN. With `newPin` the PIN is replaced first (required after a temporary PIN).
 *  Returns 'set_pin' when the PIN was temporary and a new one is needed; otherwise the reports session is in place.
 *  `remember` keeps it for 12 hours on this computer; otherwise it ends with the tab. */
export async function reportsSignIn(employeeId: string, pin: string, remember: boolean, newPin?: string): Promise<{ setPin: true; name: string } | { setPin: false; name: string }> {
  const r = await callFn<{ set_pin?: boolean; name?: string; access_token?: string; refresh_token?: string }>('reports-sign-in', { employee_id: employeeId, pin, new_pin: newPin ?? null, device: deviceId(), remember })
  if (r.set_pin) return { setPin: true, name: r.name ?? '' }
  const { error } = await supabase.auth.setSession({ access_token: r.access_token!, refresh_token: r.refresh_token! })
  if (error) throw new Error(error.message)
  try {
    localStorage.setItem(UNTIL_KEY, remember ? String(Date.now() + KEEP_HOURS * 3600_000) : 'tab')
    sessionStorage.setItem(TAB_KEY, '1')
  } catch { /* private mode */ }
  return { setPin: false, name: r.name ?? '' }
}

export async function reportsSignOut() {
  try { localStorage.removeItem(UNTIL_KEY); sessionStorage.removeItem(TAB_KEY) } catch { /* ignore */ }
  await supabase.auth.signOut({ scope: 'local' })
}

/** Is the stored session a Reports session that is still within its 12 hours (or its tab)? Signs out when it has run out. */
export async function reportsSessionState(): Promise<'reports' | 'none' | 'other'> {
  const { data } = await supabase.auth.getSession()
  const s = data.session
  if (!s) return 'none'
  const isReports = (s.user.email ?? '').toLowerCase() === REPORTS_EMAIL.toLowerCase() || !!(s.user.app_metadata as { reports?: boolean } | undefined)?.reports
  if (!isReports) return 'other'
  let until = ''
  let tab = ''
  try { until = localStorage.getItem(UNTIL_KEY) ?? ''; tab = sessionStorage.getItem(TAB_KEY) ?? '' } catch { /* ignore */ }
  const expired = until === 'tab' ? !tab : until ? Date.now() > Number(until) : false
  if (expired) { await reportsSignOut(); return 'none' }
  return 'reports'
}

/** Read one report. */
export function readReport<T = Row>(report: string, params: Row = {}) {
  return callFn<T>('reports-read', { report, ...params })
}

export function reportsAction<T = Row>(action: string, params: Row = {}) {
  return callFn<T>('reports-read', { action, ...params })
}

/** Download a view as CSV through the browser. */
export async function downloadCsv(view: string, params: Row = {}) {
  const r = await reportsAction<{ name: string; csv: string; rows: number }>('csv', { view, ...params })
  const blob = new Blob([r.csv], { type: 'text/csv;charset=utf-8' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a'); a.href = url; a.download = r.name; document.body.appendChild(a); a.click(); a.remove()
  setTimeout(() => URL.revokeObjectURL(url), 5000)
  return r.rows
}

// A small read cache so switching months back and forth is instant; every read still refreshes in the background.
const mem = new Map<string, unknown>()

export function useReport<T = Row>(report: string, params: Row = {}, deps: unknown[] = [], enabled = true) {
  const key = report + ':' + JSON.stringify(params)
  const [data, setData] = useState<T | null>(() => (mem.get(key) as T | undefined) ?? null)
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(!mem.has(key))
  const alive = useRef(true)
  const load = useCallback(async () => {
    setError('')
    if (!mem.has(key)) setLoading(true)
    try {
      const d = await readReport<T>(report, params)
      mem.set(key, d)
      if (alive.current) setData(d)
    } catch (e) { if (alive.current) setError((e as Error).message) }
    finally { if (alive.current) setLoading(false) }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key])
  useEffect(() => {
    alive.current = true
    setData((mem.get(key) as T | undefined) ?? null)
    if (enabled) load(); else setLoading(false)
    return () => { alive.current = false }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, enabled, ...deps])
  return { data, error, loading, refresh: load }
}

export function dropReportCache() { mem.clear() }
